/**
 * The Wither (vanilla Java, normal difficulty). DOM-free.
 *  - 300 HP, armor 4, 0.9 x 3.5, flies, undead; immune to fire / lava / the Wither effect.
 *  - Spawn: 220 ticks invulnerable and stationary while it heals 100 -> 300 (10 HP every 10 ticks),
 *    then an explosion of power 7 at its eye (`startSpawn`, called by game/witherSummon.ts).
 *  - Flight (WitherBoss.aiStep): hovers until >= 5 above its target, closes to within 3 blocks
 *    horizontally. At <= half health ("wither armor") arrows bounce off it and it drops to its
 *    target's level to fight in melee range.
 *  - Centre head: a skull every 40 ticks at its target within sight (0.1% blue). Side heads pick
 *    their own targets within 20 blocks and fire every 40-60 ticks, plus a random skull after
 *    ~15 idle updates. Targets: survival/adventure players and non-undead mobs.
 *  - Regenerates 1 HP / 20 ticks. 20 ticks after being hurt it smashes every breakable block in a
 *    3 x 4 x 3 volume around it (not bedrock / end portal / gateway / liquids).
 *  - Death: exactly 1 nether star, 50 XP (when a player did it).
 *
 * `view.color` carries the render state (so network replicas see it): bits 0-7 = invulnerable
 * ticks / 2, bit 8 (256) = wither armor. `view.armored` mirrors bit 8.
 */

import { AIR, BLOCKS, IS_LAVA, IS_WATERY } from '../../blocks';
import { FX, isUndeadType } from '../../game/effects/effects';
import { I } from '../../game/itemIds';
import { stackOf, type MobType, type Stack } from '../../game/types';
import type { EntityHost, PlayerTarget } from '../host';
import { Mob, type MobCtx } from '../mob';

export const WITHER_HEALTH = 300;
export const WITHER_INVUL = 220;
const WITHER_ARMOR = 4;
const FOLLOW_RANGE = 40;
const SHOOT_INTERVAL = 40;

/** Services the wither needs from the game (set by game/witherRuntime.ts, EntityManager and the smoke tests). */
export const witherHooks: {
  shoot: ((blue: boolean, x: number, y: number, z: number, dx: number, dy: number, dz: number, owner: number) => void) | null;
  /** Players it may attack (local + remote); default: the host's nearest player only. */
  players: (() => readonly PlayerTarget[]) | null;
  /** Mobs in the world (EntityManager.mobs): non-undead ones are targets. */
  mobs: (() => readonly Mob[]) | null;
  /** Explosion with entity damage (the spawn blast, power 7); default: host.explode (blocks only). */
  explode: ((x: number, y: number, z: number, power: number) => void) | null;
  /** Destroy a block with its drops; false = could not. Default: host.setBlock(x, y, z, AIR). */
  breakBlock: ((x: number, y: number, z: number) => boolean) | null;
} = { shoot: null, players: null, mobs: null, explode: null, breakBlock: null };

/** Vanilla WITHER_IMMUNE tag (what a wither can never break) plus fluids. */
const IMMUNE_NAMES = new Set(['Bedrock', 'End Portal', 'End Portal Frame', 'End Gateway', 'Barrier', 'Command Block', 'Structure Block', 'Jigsaw Block']);
export function witherCanBreak(id: number): boolean {
  if (id === AIR || IS_WATERY[id] === 1 || IS_LAVA[id] === 1) return false;
  const n = BLOCKS[id]?.name;
  return !!n && !IMMUNE_NAMES.has(n);
}

type Tgt = { p: PlayerTarget; m?: undefined } | { m: Mob; p?: undefined };
const tx = (t: Tgt): number => (t.p ? t.p.x : t.m!.x);
const ty = (t: Tgt): number => (t.p ? t.p.y : t.m!.y);
const tz = (t: Tgt): number => (t.p ? t.p.z : t.m!.z);
/** Aim point height above the target's feet: half its eye height (vanilla `getEyeHeight() * 0.5`). */
const aimY = (t: Tgt): number => ty(t) + (t.p ? t.p.eyeY - t.p.y : t.m!.eyeHeight) * 0.5;
const tAlive = (t: Tgt): boolean => (t.p ? t.p.alive && !t.p.creative && !t.p.spectator : t.m!.alive);
const same = (a: Tgt | null, b: Tgt | null): boolean => !!a && !!b && (a.p ? a.p === b.p : a.m === b.m);

export class Wither extends Mob {
  /** Spawn invulnerability ticks left (0 = fought normally). */
  invul = 0;
  /** Ticks until it breaks blocks around itself (0 = idle); set to 20 when hurt. */
  destroyTicks = 0;
  /** Chance a centre-head shot is a blue skull (vanilla 0.001). */
  blueChance = 0.001;
  /** Immune to fire and lava (checked by smoke/correctness). */
  readonly fireImmune = true;
  /** Last target (centre head). */
  target: Tgt | null = null;
  private shootCd = -1;
  private fvy = 0;
  private lastVy = 0;
  private lastY = 0;
  private readonly nextHead = [0, 0];
  private readonly idle = [0, 0];
  private readonly alt: [Tgt | null, Tgt | null] = [null, null];
  private seek = 0;
  private wx = 0; private wy = 0; private wz = 0; private wanderT = 0;
  private ticking = false;

  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'wither', x, y, z, { width: 0.9, height: 3.5, eye: 3, health: WITHER_HEALTH, speed: 0.6 }, rnd);
    this.noFallDamage = true;
    this.persistent = true; // a boss never despawns
    this.knockbackResist = 0;
    this.wx = x; this.wy = y; this.wz = z;
    this.updateColor();
  }

  /** Vanilla makeInvulnerable(): begin the 220-tick spawn sequence at a third of its health. */
  startSpawn(): void {
    this.invul = WITHER_INVUL;
    this.health = this.maxHealth / 3;
    this.updateColor();
  }

  /** Wither armor: at or below half health (also during the spawn, health starts at 100). */
  get powered(): boolean { return this.health <= this.maxHealth / 2; }

  private updateColor(): void { this.color = Math.min(255, Math.ceil(this.invul / 2)) | (this.powered ? 256 : 0); }

  protected override floats(): boolean { return false; }
  override onHurtBy(fromX: number, fromZ: number): void {
    // HurtByTargetGoal: turn on the player who swung at it
    let best: Tgt | null = null, bd = 64;
    for (const p of this.playersList()) {
      if (!p.alive || p.creative || p.spectator) continue;
      const d = (p.x - fromX) ** 2 + (p.z - fromZ) ** 2;
      if (d < bd) { bd = d; best = { p }; }
    }
    if (best) this.target = best;
  }
  xpValue(): number { return 50; }
  loot(_rnd: () => number): Stack[] { return [stackOf(I.NETHER_STAR, 1)]; }

  /** Arrows bounce off the wither armor (EntityManager arrowCtx asks before dealing damage). */
  dodgeProjectile(): boolean { return !this.dying && this.powered; }

  override tick(ctx: MobCtx): void {
    this.fireTicks = 0;
    this.effects?.remove(FX.WITHER); // immune to the Wither effect
    this.ticking = true;
    try { super.tick(ctx); } finally { this.ticking = false; }
    this.fireTicks = 0;
    this.updateColor();
  }

  override hurt(amount: number): boolean {
    if (!this.alive || amount <= 0) return false;
    if (this.invul > 0) return false;
    // immune to fire and lava (the base class' lava / burning damage runs inside tick())
    if (this.ticking && (this.inLava || this.fireTicks > 0)) { this.fireTicks = 0; return false; }
    // armor 4, toughness 0: damage * (1 - clamp(armor - damage / 2, armor / 5, 20) / 25)
    amount *= 1 - Math.min(20, Math.max(WITHER_ARMOR / 5, WITHER_ARMOR - amount / 2)) / 25;
    const r = super.hurt(amount);
    if (r) {
      if (this.destroyTicks <= 0) this.destroyTicks = 20;
      this.idle[0] = this.idle[1] = 0;
    }
    return r;
  }

  saveExtra(): unknown { return [this.invul, this.destroyTicks]; }
  loadExtra(d: unknown): void {
    const a = d as number[];
    this.invul = typeof a?.[0] === 'number' ? Math.max(0, Math.min(WITHER_INVUL, a[0])) : 0;
    this.destroyTicks = typeof a?.[1] === 'number' ? Math.max(0, a[1]) : 0;
    this.updateColor();
  }

  override syncView(host: EntityHost): void {
    super.syncView(host);
    this.view.armored = this.powered;
  }

  // ------------------------------------------------------------------ AI

  private playersList(): readonly PlayerTarget[] {
    return witherHooks.players ? witherHooks.players() : [this.host.player as unknown as PlayerTarget];
  }

  /** Candidate targets within `range`, nearest first: survival players and non-undead mobs. */
  private candidates(range: number): Tgt[] {
    const out: { t: Tgt; d: number }[] = [];
    const cy = this.y + this.height / 2;
    for (const p of this.playersList()) {
      if (!p.alive || p.creative || p.spectator) continue;
      const d = (p.x - this.x) ** 2 + (p.y + 0.9 - cy) ** 2 + (p.z - this.z) ** 2;
      if (d <= range * range) out.push({ t: { p }, d });
    }
    for (const m of witherHooks.mobs?.() ?? []) {
      if ((m as Mob) === this || !m.alive || isUndeadType(m.type)) continue;
      const d = (m.x - this.x) ** 2 + (m.y + m.height / 2 - cy) ** 2 + (m.z - this.z) ** 2;
      if (d <= range * range) out.push({ t: { m }, d });
    }
    out.sort((a, b) => a.d - b.d);
    return out.map((o) => o.t);
  }

  /** Head position (vanilla getHeadX/Y/Z): 0 = centre, 1 / 2 = the side heads, 1.3 blocks to either side. */
  headPos(i: number): [number, number, number] {
    if (i <= 0) return [this.x, this.y + 3, this.z];
    const side = i === 1 ? -1 : 1;
    return [this.x + Math.cos(this.yaw) * 1.3 * side, this.y + 2.2, this.z - Math.sin(this.yaw) * 1.3 * side];
  }

  /** performRangedAttack(i, x, y, z, dangerous): launch a skull from head `i` at a point. */
  shootAt(i: number, x: number, y: number, z: number, blue: boolean): void {
    const [hx, hy, hz] = this.headPos(i);
    witherHooks.shoot?.(blue, hx, hy, hz, x - hx, y - hy, z - hz, this.id);
  }

  private breakBlocks(ctx: MobCtx): void {
    const cx = Math.floor(this.x), cy = Math.floor(this.y), cz = Math.floor(this.z);
    const brk = witherHooks.breakBlock ?? ((x: number, y: number, z: number): boolean => {
      if (!ctx.host.setBlock) return false;
      ctx.host.setBlock(x, y, z, AIR);
      return true;
    });
    for (let dx = -1; dx <= 1; dx++) for (let dz = -1; dz <= 1; dz++) for (let dy = 0; dy <= 3; dy++) {
      const x = cx + dx, y = cy + dy, z = cz + dz;
      if (witherCanBreak(ctx.world.getBlock(x, y, z))) brk(x, y, z);
    }
  }

  protected ai(ctx: MobCtx): void {
    const r = ctx.host.rnd;
    if (ctx.host.difficulty?.() === 'peaceful') { this.removed = true; return; }

    // --- spawn sequence: stationary, invulnerable, healing 10 every 10 ticks
    if (this.invul > 0) {
      this.vx = this.vy = this.vz = 0; this.fvy = 0;
      const i = this.invul - 1;
      if (i <= 0) {
        const ex = this.x, ey = this.eyeY, ez = this.z;
        if (witherHooks.explode) witherHooks.explode(ex, ey, ez, 7); else ctx.host.explode(ex, ey, ez, 7);
      }
      this.invul = i;
      if (this.age % 10 === 0) this.health = Math.min(this.maxHealth, this.health + 10);
      return;
    }

    if (this.age % 20 === 0) this.health = Math.min(this.maxHealth, this.health + 1);
    if (this.destroyTicks > 0 && --this.destroyTicks === 0) this.breakBlocks(ctx);

    // --- target: keep it while valid and within follow range, else the nearest candidate
    const cy = this.y + this.height / 2;
    const t = this.target;
    const valid = t && tAlive(t) && (tx(t) - this.x) ** 2 + (ty(t) + 0.9 - cy) ** 2 + (tz(t) - this.z) ** 2 <= FOLLOW_RANGE * FOLLOW_RANGE;
    if (!valid) {
      this.target = null;
      if (this.seek-- <= 0) {
        this.seek = 5;
        this.target = this.candidates(FOLLOW_RANGE)[0] ?? null;
      }
    }
    const tgt = this.target;

    // --- movement (WitherBoss.aiStep): flight state is set directly, travel() only applies collisions
    if (Math.abs(this.y - this.lastY) < Math.abs(this.lastVy) * 0.25) this.fvy = 0; // blocked above / below
    this.lastY = this.y;
    let dy = this.fvy * 0.6;
    let gx: number, gy: number, gz: number;
    if (tgt) { gx = tx(tgt); gy = ty(tgt); gz = tz(tgt); }
    else {
      if (--this.wanderT <= 0 || (this.wx - this.x) ** 2 + (this.wz - this.z) ** 2 < 4) {
        this.wanderT = 80 + Math.floor(r() * 60);
        this.wx = this.x + (r() * 2 - 1) * 12; this.wz = this.z + (r() * 2 - 1) * 12;
        this.wy = Math.max(this.y - 6, Math.min(this.y + 4, this.y + (r() * 2 - 1) * 5));
      }
      gx = this.wx; gy = this.wy; gz = this.wz;
    }
    if (tgt && this.powered) {
      // wither armor: come down to the target's level and into melee range
      const want = Math.max(-0.35, Math.min(0.35, (gy + 0.5 - this.y) * 0.25));
      dy += (want - dy) * 0.3;
    } else if (this.y < (tgt ? gy + 5 : gy)) { // hover >= 5 above the target (vanilla: y < ty || !powered && y < ty + 5)
      dy = Math.max(0, dy);
      dy += 0.3 - dy * 0.6;
    }
    const hx = gx - this.x, hz = gz - this.z;
    const hd2 = hx * hx + hz * hz;
    if (hd2 > (tgt && this.powered ? 4 : 9)) {
      const hl = Math.sqrt(hd2);
      this.vx += (hx / hl) * 0.3 - this.vx * 0.6;
      this.vz += (hz / hl) * 0.3 - this.vz * 0.6;
    }
    this.vy = dy;
    this.lastVy = dy;
    this.fvy = dy * 0.91;
    if (tgt) {
      this.faceToward(gx, gz);
      this.lookAt(gx, aimY(tgt), gz);
      this.aggressive = true;
    } else if (Math.hypot(this.vx, this.vz) > 0.05) this.faceToward(this.x + this.vx, this.z + this.vz);

    // --- centre head: a skull every 40 ticks at a target it can see
    if (tgt) {
      const [hx0, hy0, hz0] = this.headPos(0);
      if (--this.shootCd === 0) {
        if (ctx.los(hx0, hy0, hz0, tx(tgt), aimY(tgt), tz(tgt))) {
          this.shootAt(0, tx(tgt), aimY(tgt), tz(tgt), r() < this.blueChance);
          this.shootCd = SHOOT_INTERVAL;
        } else this.shootCd = SHOOT_INTERVAL; // vanilla re-arms the interval after a blocked shot
      } else if (this.shootCd < 0) this.shootCd = SHOOT_INTERVAL;
    } else this.shootCd = -1;

    // --- side heads (normal / hard): own targets, 40-60 ticks between shots, random skulls when idle
    for (let i = 0; i < 2; i++) {
      if (this.age < this.nextHead[i]) continue;
      this.nextHead[i] = this.age + 10 + Math.floor(r() * 10);
      if (++this.idle[i] > 15) {
        this.shootAt(i + 1, this.x + (r() * 20 - 10), this.y + (r() * 10 - 5), this.z + (r() * 20 - 10), true); // vanilla: the idle heads' random skulls are blue
        this.idle[i] = 0;
      }
      const a = this.alt[i];
      const [sx, sy, sz] = this.headPos(i + 1);
      if (a) {
        if (!tAlive(a) || (tx(a) - this.x) ** 2 + (ty(a) - this.y) ** 2 + (tz(a) - this.z) ** 2 > 900 || !ctx.los(sx, sy, sz, tx(a), aimY(a), tz(a))) this.alt[i] = null;
        else {
          this.shootAt(i + 1, tx(a), aimY(a), tz(a), false);
          this.nextHead[i] = this.age + 40 + Math.floor(r() * 20);
          this.idle[i] = 0;
        }
      } else {
        const seen = this.candidates(20).filter((c) => ctx.los(sx, sy, sz, tx(c), aimY(c), tz(c)) && !same(c, this.alt[1 - i]));
        if (seen.length) this.alt[i] = seen[Math.floor(r() * seen.length)];
      }
    }
  }
}

/** Factory for EntityManager.spawnMob. */
export function createWitherMob(type: MobType, id: number, x: number, y: number, z: number, rnd: () => number): Mob | null {
  return type === 'wither' ? new Wither(id, x, y, z, rnd) : null;
}
